/**
 * WHICH REVIEW PAGE TO SEND SOMEBODY TO.
 *
 * Anthony: "link to review should go either to [three Google links] or the
 * toledo or havasu or portland pages depending which is closer to the
 * customer."
 *
 * A review only counts on the listing for the market it was earned in. A
 * customer in Lake Havasu leaving five stars on the Hemet listing helps
 * Hemet's ranking and does nothing for Havasu, which is the market that
 * just did the work.
 *
 * HOW THE MARKET IS DECIDED, in the order the answers can be trusted:
 *
 *   1. THE TRACKING NUMBER THEY RANG. CallRail lines are one per market,
 *      so this is not a guess — a call to the Havasu line is a Havasu job.
 *      This is the same reasoning the old dispatch routing used, and for
 *      the same reason: caller ID city is carrier data and is wrong often
 *      enough to be useless.
 *   2. THE AREA CODE they called from. Weaker — people move and keep their
 *      number — but better than nothing.
 *   3. THE SHOP'S HOME MARKET, when neither says anything.
 *
 * Every URL is overridable by environment variable, because a Google
 * listing link changes when a listing does and that must never need a
 * deploy.
 */

export type Market = {
  key: string;
  label: string;
  /** CallRail lines that belong to this market. */
  tracking: string[];
  /** Area codes that suggest it, when the tracking number says nothing. */
  areaCodes: string[];
  review: string;
};

const digits = (s: string | null | undefined) => String(s ?? "").replace(/\D/g, "");
const last10 = (s: string | null | undefined) => digits(s).slice(-10);

/**
 * The three Google links Anthony gave are assigned to the three California
 * lines, which is where the shop does most of its work. If any of them
 * belongs to a different listing, it is one environment variable to move —
 * REVIEW_URL_<KEY> — and nothing here needs editing.
 */
export const MARKETS: Market[] = [
  {
    key: "hemet", label: "Hemet and San Jacinto",
    tracking: ["9514741277", "9517203622", "9519042477", "9516441599", "9512678500", "9517202842"],
    areaCodes: ["951"],
    review: "https://share.google/1mHi9Mkm0xGrUAqsl",
  },
  {
    key: "riverside", label: "Riverside and Temecula",
    tracking: [],
    areaCodes: ["909", "760"],
    review: "https://maps.app.goo.gl/rgFkgL6wd4BtsYLg8",
  },
  {
    key: "losangeles", label: "Los Angeles",
    tracking: ["3103406775"],
    areaCodes: ["310", "213", "323", "424", "562", "626", "818"],
    review: "https://share.google/tgSrtmnzUkQzFrw5c",
  },
  {
    key: "havasu", label: "Lake Havasu",
    tracking: ["9285045772"],
    areaCodes: ["928"],
    review: "https://havasumechanic.com",
  },
  {
    key: "portland", label: "Portland",
    tracking: ["9715252111"],
    areaCodes: ["971", "503"],
    review: "https://portlandmobilemechanic.com",
  },
  {
    key: "toledo", label: "Toledo",
    tracking: [],
    areaCodes: ["419", "567"],
    review: "https://toledomobilemechanic.com",
  },
];

export const HOME = MARKETS[0]!;

/**
 * Pick one. `overrides` comes from the server so a link can be changed
 * without a deploy — it is keyed `REVIEW_URL_HEMET` and so on.
 */
export function reviewFor(
  opts: { trackingNumber?: string | null; customerPhone?: string | null },
  overrides: Record<string, string> = {},
): { market: Market; url: string; why: string } {
  const url = (m: Market) => overrides[m.key] || m.review;

  const line = last10(opts.trackingNumber);
  if (line) {
    const byLine = MARKETS.find((m) => m.tracking.includes(line));
    if (byLine) {
      return { market: byLine, url: url(byLine), why: `they rang the ${byLine.label} line` };
    }
  }

  const area = last10(opts.customerPhone).slice(0, 3);
  if (area) {
    const byArea = MARKETS.find((m) => m.areaCodes.includes(area));
    if (byArea) {
      return { market: byArea, url: url(byArea), why: `their number is a ${area} one` };
    }
  }

  return { market: HOME, url: url(HOME), why: "nothing said which market, so the main listing" };
}

/**
 * EVERY MARKET ABOVE IS SUPERCANIC'S, and only Supercanic may use them.
 *
 * Anthony, 2026-09-25: "correct shops like Huey's Mobile to direct a review
 * link to their Google My Business page. Not our Supercanic ones please.
 * Brians customer left us a review lol". Huey's Mobile had pressed "Send
 * the review link" and texted its customer the Hemet listing, because
 * reviewFor() never asked whose job it was. Same bug as the warranty in
 * src/domain/warranty.ts: a thing that belongs to one shop, handed to all.
 *
 * So a shop's own link (accounts.review_url, migration 0170) wins. Without
 * one, Supercanic falls back to its market routing and EVERY OTHER SHOP
 * GETS NO LINK AT ALL. A missing link costs one review. The wrong link
 * gives that review to somebody else's business.
 */
export const SUPERCANIC_ACCOUNT = "7097c673-aa9f-4f0e-ba24-75050a5a29ae";

export type ReviewPick =
  | { url: string; label: string; why: string }
  | { url: null; label: null; why: string };

export function reviewForShop(
  shop: { id: string | null; review_url: string | null } | null,
  opts: { trackingNumber?: string | null; customerPhone?: string | null },
  overrides: Record<string, string> = {},
): ReviewPick {
  const own = (shop?.review_url ?? "").trim();
  if (/^https?:\/\/\S+$/i.test(own)) {
    return { url: own, label: "Your Google", why: "the review link in Settings" };
  }
  if (shop?.id === SUPERCANIC_ACCOUNT) {
    const r = reviewFor(opts, overrides);
    return { url: r.url, label: r.market.label, why: r.why };
  }
  return { url: null, label: null, why: "no review link is set for this shop" };
}
